import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Card,
  Divider,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Checkbox,
  CircularProgress,
  Typography,
  Box,
  ListSubheader,
  Chip,
  Paper,
  Fade,
  Slide,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import UserLogOut from "./UserLogOut";
import PublishIcon from "@mui/icons-material/Publish";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ClearIcon from "@mui/icons-material/Clear";
import BusinessIcon from "@mui/icons-material/Business";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ManhourShiftEnter = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [username] = useState(
    location.state?.username || sessionStorage.getItem("username") || ""
  );

  const [machineGroups, setMachineGroups] = useState([]);
  const [groupedMachines, setGroupedMachines] = useState([]);
  const [selectedMachineCodes, setSelectedMachineCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (username) {
      sessionStorage.setItem("username", username);
    }
  }, [username]);

  // Fetch machine groups and all machines
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch machine groups
        const groupsRes = await axios.get(`${API_BASE_URL}/MachineGroups/${username}`);
        const groups = groupsRes.data || [];
        setMachineGroups(groups);

        // Fetch all machines for all groups
        const machinePromises = groups.map((groupCode) =>
          axios.get(`${API_BASE_URL}/ManhourMachineNames?groupCode=${groupCode}`)
        );
        const machineResponses = await Promise.all(machinePromises);

        // Group machines by their group code
        const grouped = [];
        groups.forEach((groupCode, index) => {
          const machines = machineResponses[index]?.data || [];
          if (machines.length > 0) {
            // Add group header
            grouped.push({
              isGroupHeader: true,
              groupName: groupCode,
            });
            // Add machines for this group
            machines.forEach((machine) => {
              grouped.push({
                ...machine,
                isGroupHeader: false,
                groupCode: groupCode,
              });
            });
          }
        });

        setGroupedMachines(grouped);
      } catch (err) {
        console.error("Error fetching data:", err);
        setSnackbar({
          open: true,
          message: "Error loading workstations. Please try again.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchData();
    }
  }, [username]);

  const handleSubmit = () => {
    if (selectedMachineCodes.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one workstation to continue.",
        severity: "warning",
      });
      return;
    }
    setDialogOpen(true);
  };

  const handleDialogClose = async (confirm) => {
    setDialogOpen(false);
    if (confirm) {
      try {
        setIsSubmitting(true);

        // Filter selected machines with full info
        const selectedMachines = groupedMachines.filter(
          (machine) =>
            !machine.isGroupHeader &&
            selectedMachineCodes.includes(machine.MachineCode)
        );

        const formattedMachines = selectedMachines.map((machine) => ({
          machineCode: machine.MachineCode,
          groupCode: machine.groupCode,
          finishedGoodStatus: machine.FinishedGood || 0,
        }));

        // Submit to backend
        const response = await axios.post(
          `${API_BASE_URL}/manhourSupervisorSelectStation`,
          {
            supervisorId: username,
            machineCodes: selectedMachineCodes,
          }
        );

        if (response.status === 200) {
          setSnackbar({
            open: true,
            message: "🎉 Workstation selection submitted successfully!",
            severity: "success",
          });

          setTimeout(() => {
            navigate("/SupervisorMainPage", {
              state: {
                supervisorId: username,
                machines: formattedMachines,
              },
            });
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to submit workstation selection. Please try again.",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error submitting workstation selection:", error);
        setSnackbar({
          open: true,
          message: "An error occurred. Please try again.",
          severity: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const clearAllSelections = () => {
    setSelectedMachineCodes([]);
  };

  // Filter out group headers for value comparison
  const machineOptions = groupedMachines.filter((item) => !item.isGroupHeader);
  const selectedGroupCodes = [
    ...new Set(
      machineOptions
        .filter((m) => selectedMachineCodes.includes(m.MachineCode))
        .map((m) => m.groupCode)
    ),
  ];

  if (loading) {
    return (
      <Box
        sx={(t) => ({
          height: "100vh",
          boxSizing: "border-box",
          background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        })}
      >
        <Card sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading workstations...
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={(t) => ({
        height: "100vh",
        boxSizing: "border-box",
        background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 3,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 20% 80%, ${alpha(t.palette.primary.main, 0.3)} 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${alpha(t.palette.common.white, 0.12)} 0%, transparent 50%)
          `,
          pointerEvents: "none",
        },
      })}
    >
      <Fade in timeout={800}>
        <Card
          sx={(t) => ({
            width: "100%",
            maxWidth: 700,
            maxHeight: "calc(100vh - 48px)",
            overflow: "auto",
            padding: 4,
            borderRadius: 4,
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
            bgcolor: alpha(t.palette.background.paper, 0.95),
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(t.palette.divider, 0.4)}`,
            position: "relative",
            zIndex: 1,
          })}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              sx={(t) => ({
                fontWeight: 800,
                background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
              })}
            >
              SHIFT SETUP
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Select your workstations to begin monitoring
            </Typography>
          </Box>

          <Divider
            sx={(t) => ({
              mb: 4,
              background: `linear-gradient(90deg, transparent, ${t.palette.primary.main}, transparent)`,
            })}
          />

          {/* Stats Cards */}
          <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
            <Paper
              elevation={2}
              sx={(t) => ({
                flex: 1,
                p: 2,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
                color: t.palette.common.white,
              })}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BusinessIcon />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {machineGroups.length}
                  </Typography>
                  <Typography variant="caption">Machine Groups</Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              elevation={2}
              sx={(t) => ({
                flex: 1,
                p: 2,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${t.palette.info.main} 0%, ${t.palette.info.light} 100%)`,
                color: t.palette.getContrastText(t.palette.info.main),
              })}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GroupWorkIcon />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {machineOptions.length}
                  </Typography>
                  <Typography variant="caption">Total Workstations</Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              elevation={2}
              sx={(t) => ({
                flex: 1,
                p: 2,
                borderRadius: 3,
                background:
                  selectedMachineCodes.length > 0
                    ? `linear-gradient(135deg, ${t.palette.success.main} 0%, ${t.palette.success.light} 100%)`
                    : `linear-gradient(135deg, ${alpha(t.palette.text.disabled, 0.4)} 0%, ${alpha(
                        t.palette.text.disabled,
                        0.6
                      )} 100%)`,
                color:
                  selectedMachineCodes.length > 0
                    ? t.palette.getContrastText(t.palette.success.main)
                    : t.palette.getContrastText(t.palette.text.disabled),
              })}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedMachineCodes.length}
                  </Typography>
                  <Typography variant="caption">Selected</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Workstation Selector */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
                Choose Workstations
              </Typography>
              {selectedMachineCodes.length > 0 && (
                <Tooltip title="Clear all selections">
                  <IconButton
                    onClick={clearAllSelections}
                    size="small"
                    sx={(t) => ({
                      color: t.palette.error.main,
                      "&:hover": {
                        backgroundColor: alpha(t.palette.error.main, 0.15),
                        color: t.palette.error.contrastText,
                      },
                    })}
                  >
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Autocomplete
              multiple
              options={groupedMachines}
              value={machineOptions.filter((m) =>
                selectedMachineCodes.includes(m.MachineCode)
              )}
              onChange={(event, newValue) => {
                const validSelections = newValue.filter((item) => !item.isGroupHeader);
                setSelectedMachineCodes(validSelections.map((item) => item.MachineCode));
              }}
              getOptionLabel={(option) =>
                option.isGroupHeader
                  ? option.groupName
                  : `${option.MachineDescription} - ${option.MachineCode}`
              }
              getOptionDisabled={(option) => option.isGroupHeader}
              isOptionEqualToValue={(option, value) =>
                !option.isGroupHeader &&
                !value.isGroupHeader &&
                option.MachineCode === value.MachineCode
              }
              renderOption={(props, option, { selected }) => {
                if (option.isGroupHeader) {
                  return (
                    <ListSubheader
                      key={option.groupName}
                      sx={(t) => ({
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        color: "primary.main",
                        background: `linear-gradient(135deg, ${alpha(
                          t.palette.primary.main,
                          0.1
                        )} 0%, ${alpha(t.palette.secondary.main, 0.1)} 100%)`,
                        lineHeight: "48px",
                        borderRadius: 1,
                        margin: "4px 8px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      })}
                    >
                      <BusinessIcon fontSize="small" />
                      {option.groupName}
                    </ListSubheader>
                  );
                }
                return (
                  <li {...props} key={option.MachineCode} style={{ padding: "8px 16px" }}>
                    <Checkbox
                      checked={selected}
                      sx={(t) => ({
                        mr: 2,
                        "&.Mui-checked": {
                          color: t.palette.primary.main,
                        },
                      })}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.MachineDescription}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Code: {option.MachineCode}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Workstations"
                  placeholder="Search and select workstations..."
                  variant="outlined"
                  sx={(t) => ({
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: alpha(t.palette.background.paper, 0.98),
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: t.palette.primary.main,
                      },
                    },
                  })}
                />
              )}
              ListboxProps={{
                sx: {
                  maxHeight: 350,
                  borderRadius: 2,
                  "& .MuiAutocomplete-option": {
                    borderRadius: 1,
                    margin: "2px 8px",
                  },
                },
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="filled"
                    label={option.MachineCode}
                    {...getTagProps({ index })}
                    sx={(t) => ({
                      background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
                      color: t.palette.common.white,
                      fontWeight: 600,
                      "& .MuiChip-deleteIcon": {
                        color: "rgba(255, 255, 255, 0.7)",
                        "&:hover": {
                          color: t.palette.common.white,
                        },
                      },
                    })}
                  />
                ))
              }
            />
          </Box>

          {/* Selected workstations summary */}
          {selectedMachineCodes.length > 0 && (
            <Slide direction="up" in={selectedMachineCodes.length > 0} timeout={300}>
              <Paper
                elevation={2}
                sx={(t) => ({
                  mb: 4,
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(
                    t.palette.success.main,
                    0.12
                  )} 0%, ${alpha(t.palette.info.light, 0.12)} 100%)`,
                  border: `1px solid ${alpha(t.palette.success.main, 0.3)}`,
                })}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <CheckCircleIcon sx={{ color: "success.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: "success.main" }}>
                    Selection Summary
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Workstations ({selectedMachineCodes.length})
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedMachineCodes.map((code) => (
                        <Chip
                          key={code}
                          label={code}
                          size="small"
                          sx={(t) => ({
                            backgroundColor: t.palette.success.light,
                            color: t.palette.success.contrastText,
                            fontWeight: 600,
                          })}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Groups ({selectedGroupCodes.length})
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedGroupCodes.map((group) => (
                        <Chip
                          key={group}
                          label={group}
                          size="small"
                          icon={<BusinessIcon />}
                          sx={(t) => ({
                            backgroundColor: t.palette.primary.light,
                            color: t.palette.primary.contrastText,
                            fontWeight: 600,
                          })}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Slide>
          )}

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <UserLogOut username={username} />
            <Button
              variant="contained"
              size="large"
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PublishIcon />
                )
              }
              onClick={handleSubmit}
              disabled={isSubmitting || selectedMachineCodes.length === 0}
              sx={(t) => ({
                borderRadius: 6,
                px: 4,
                py: 1.5,
                minWidth: 180,
                background:
                  selectedMachineCodes.length > 0
                    ? `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`
                    : `linear-gradient(135deg, ${alpha(t.palette.text.disabled, 0.4)} 0%, ${alpha(
                        t.palette.text.disabled,
                        0.6
                      )} 100%)`,
                boxShadow:
                  selectedMachineCodes.length > 0
                    ? `0 8px 32px ${alpha(t.palette.primary.main, 0.3)}`
                    : "none",
                fontSize: "1.1rem",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": {
                  background:
                    selectedMachineCodes.length > 0
                      ? `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.secondary.dark} 100%)`
                      : `linear-gradient(135deg, ${alpha(t.palette.text.disabled, 0.4)} 0%, ${alpha(
                          t.palette.text.disabled,
                          0.6
                        )} 100%)`,
                  transform: selectedMachineCodes.length > 0 ? "translateY(-2px)" : "none",
                  boxShadow:
                    selectedMachineCodes.length > 0
                      ? `0 12px 40px ${alpha(t.palette.primary.main, 0.4)}`
                      : "none",
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              })}
            >
              {isSubmitting ? "Processing..." : "Start Shift"}
            </Button>
          </Box>
        </Card>
      </Fade>

      {/* Confirm Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => handleDialogClose(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: (t) => ({
            borderRadius: 4,
            background: alpha(t.palette.background.paper, 0.95),
            backdropFilter: "blur(20px)",
          }),
        }}
      >
        <DialogTitle
          sx={(t) => ({
            display: "flex",
            alignItems: "center",
            gap: 2,
            background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
            color: t.palette.common.white,
            borderRadius: "16px 16px 0 0",
          })}
        >
          <WarningAmberIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Confirm Shift Setup
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pb: 1 }}>
          <Paper
            elevation={1}
            sx={(t) => ({
              p: 3,
              borderRadius: 3,
              backgroundColor: alpha(t.palette.grey[50], t.palette.mode === "dark" ? 0.06 : 1),
            })}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
            >
              📋 Shift Details
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Selected Workstations ({selectedMachineCodes.length})
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {selectedMachineCodes.map((code) => (
                  <Chip key={code} label={code} size="small" color="primary" />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Machine Groups ({selectedGroupCodes.length})
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {selectedGroupCodes.map((group) => (
                  <Chip key={group} label={group} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={() => handleDialogClose(false)}
            variant="outlined"
            sx={(t) => ({
              borderRadius: 3,
              px: 3,
              borderColor: t.palette.error.main,
              color: t.palette.error.main,
              "&:hover": {
                backgroundColor: alpha(t.palette.error.main, 0.15),
                color: t.palette.error.contrastText,
              },
            })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDialogClose(true)}
            variant="contained"
            autoFocus
            sx={(t) => ({
              borderRadius: 3,
              px: 4,
              background: `linear-gradient(135deg, ${t.palette.success.main} 0%, ${t.palette.info.light} 100%)`,
              "&:hover": {
                background: `linear-gradient(135deg, ${t.palette.success.dark} 0%, ${t.palette.info.main} 100%)`,
              },
            })}
          >
            Confirm & Start
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: 3,
            fontWeight: 600,
            fontSize: "1rem",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManhourShiftEnter;
